import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Cookie, Shield, BarChart3, Target, Users, Settings, 
  ChevronDown, ChevronUp, Check, X, ExternalLink, ArrowLeft
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';

// Cookie categories with detailed descriptions
const COOKIE_CATEGORIES = [
  {
    id: 'essential',
    name: 'Essential Cookies',
    description: 'These cookies are strictly necessary for the operation of our website. They enable core functionality such as security, network management, and account access. You cannot opt out of these cookies as the website cannot function properly without them.',
    required: true,
    icon: Shield,
    cookies: [
      { name: 'bc_session', purpose: 'Maintains your session state across page requests', duration: 'Session' },
      { name: 'bc_auth_token', purpose: 'Authenticates your account login status', duration: '24 hours' },
      { name: 'bc_csrf', purpose: 'Prevents cross-site request forgery attacks', duration: 'Session' },
    ]
  },
  {
    id: 'functional',
    name: 'Functional Cookies',
    description: 'These cookies enable enhanced functionality and personalization, such as remembering your preferences, language settings, and customization choices. If you disable these cookies, some features may not work as intended.',
    required: false,
    icon: Settings,
    cookies: [
      { name: 'bc_theme', purpose: 'Remembers your light/dark mode preference', duration: '1 year' },
      { name: 'bc_cart', purpose: 'Stores your shopping cart contents', duration: '30 days' },
      { name: 'bc_favorites', purpose: 'Remembers your favorite menu items', duration: '1 year' },
      { name: 'bc_language', purpose: 'Stores your preferred language setting', duration: '1 year' },
    ]
  },
  {
    id: 'analytics',
    name: 'Analytics Cookies',
    description: 'These cookies collect information about how you use our website, including which pages you visit most often and if you encounter any errors. This data helps us improve our website performance and user experience. All information is aggregated and anonymous.',
    required: false,
    icon: BarChart3,
    cookies: [
      { name: 'bc_visitor_id', purpose: 'Assigns a unique identifier to track your visits anonymously', duration: '2 years' },
      { name: 'bc_session_id', purpose: 'Groups your page views into a single session', duration: 'Session' },
      { name: 'bc_analytics_consent', purpose: 'Records your analytics consent preference', duration: '1 year' },
    ]
  },
  {
    id: 'marketing',
    name: 'Marketing Cookies',
    description: 'These cookies are used to track visitors across websites to display relevant advertisements. They help us measure the effectiveness of our marketing campaigns and limit the number of times you see an advertisement.',
    required: false,
    icon: Target,
    cookies: [
      { name: 'bc_campaign_source', purpose: 'Tracks which marketing campaign brought you to our site', duration: '90 days' },
      { name: 'bc_utm_data', purpose: 'Stores marketing attribution parameters', duration: '30 days' },
    ]
  },
  {
    id: 'social',
    name: 'Social Media Cookies',
    description: 'These cookies are set by social media services that we have added to the site to enable you to share our content with your friends and networks. They may track your browser across other sites and build a profile of your interests.',
    required: false,
    icon: Users,
    cookies: [
      { name: 'Social sharing preferences', purpose: 'Enables sharing to social platforms', duration: 'Varies' },
    ]
  }
];

const CookiePolicyPage = () => {
  const navigate = useNavigate();
  const [expandedCategories, setExpandedCategories] = useState(['essential']);
  const [preferences, setPreferences] = useState(() => {
    const saved = localStorage.getItem('bc_cookie_preferences');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return { essential: true, functional: true, analytics: false, marketing: false, social: false };
      }
    }
    return { essential: true, functional: true, analytics: false, marketing: false, social: false };
  });
  const [saved, setSaved] = useState(false);

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const togglePreference = (categoryId) => {
    if (categoryId === 'essential') return; // Cannot disable essential cookies
    setPreferences(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
    setSaved(false);
  };

  const acceptAll = () => {
    const allEnabled = { essential: true, functional: true, analytics: true, marketing: true, social: true };
    setPreferences(allEnabled);
    savePreferences(allEnabled);
  };

  const rejectNonEssential = () => {
    const essentialOnly = { essential: true, functional: false, analytics: false, marketing: false, social: false };
    setPreferences(essentialOnly);
    savePreferences(essentialOnly);
  };

  const savePreferences = (prefs = preferences) => {
    localStorage.setItem('bc_cookie_preferences', JSON.stringify(prefs));
    document.cookie = `bc_cookie_preferences=${JSON.stringify(prefs)}; max-age=${365 * 24 * 60 * 60}; path=/; SameSite=Lax`;
    
    // Update analytics consent
    if (prefs.analytics) {
      document.cookie = 'bc_analytics_consent=true; max-age=31536000; path=/; SameSite=Lax';
      window.dispatchEvent(new CustomEvent('analyticsConsentGiven'));
    } else {
      document.cookie = 'bc_analytics_consent=false; max-age=31536000; path=/; SameSite=Lax';
    }
    
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const lastUpdated = 'April 9, 2026';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <Helmet>
        <title>Cookie Policy | Bulaccino Cafe Nadi, Fiji</title>
        <meta name="description" content="Cookie Policy for Bulaccino Cafe. Learn about the cookies we use on our website and how to manage your cookie preferences." />
        <link rel="canonical" href="https://bulaccinocafe.com/cookies" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <main className="pt-8 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
                <Cookie className="h-8 w-8 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Cookie Policy</h1>
                <p className="text-gray-500 dark:text-gray-400">Last updated: {lastUpdated}</p>
              </div>
            </div>
          </motion.div>

          {/* Introduction */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-900 rounded-2xl p-6 md:p-8 shadow-sm border border-gray-200 dark:border-gray-800 mb-6"
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">About This Cookie Policy</h2>
            <div className="prose prose-gray dark:prose-invert max-w-none text-sm">
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                This Cookie Policy explains how Bulaccino Cafe ("we", "us", or "our") uses cookies and similar tracking 
                technologies when you visit our website at bulaccinocafe.com (the "Website"). This policy provides you with 
                clear and comprehensive information about the cookies we use and the purposes for using them.
              </p>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                By continuing to browse or use our Website, you agree to our use of cookies as described in this Cookie Policy. 
                You can manage your cookie preferences at any time using the controls below or by adjusting your browser settings.
              </p>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                This Cookie Policy should be read in conjunction with our <a href="/privacy-policy" className="text-teal-600 hover:underline">Privacy Policy</a> and 
                our <a href="/terms-of-use" className="text-teal-600 hover:underline">Terms of Use</a>.
              </p>
            </div>
          </motion.section>

          {/* What Are Cookies */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-white dark:bg-gray-900 rounded-2xl p-6 md:p-8 shadow-sm border border-gray-200 dark:border-gray-800 mb-6"
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">What Are Cookies?</h2>
            <div className="prose prose-gray dark:prose-invert max-w-none text-sm">
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                Cookies are small text files that are stored on your device (computer, tablet, or mobile phone) when you visit 
                a website. They are widely used to make websites work more efficiently, provide a better user experience, and 
                supply information to the owners of the website.
              </p>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                <strong className="text-gray-900 dark:text-white">First-party cookies</strong> are set by the website you are visiting. 
                <strong className="text-gray-900 dark:text-white"> Third-party cookies</strong> are set by a domain other than the website 
                you are visiting, typically used for advertising and analytics purposes.
              </p>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                Cookies can be <strong className="text-gray-900 dark:text-white">"persistent"</strong> (remaining on your device for a 
                set period or until manually deleted) or <strong className="text-gray-900 dark:text-white">"session"</strong> (deleted 
                automatically when you close your browser).
              </p>
            </div>
          </motion.section>

          {/* Cookie Preferences Manager */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-900 rounded-2xl p-6 md:p-8 shadow-sm border border-gray-200 dark:border-gray-800 mb-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Manage Your Cookie Preferences</h2>
              {saved && (
                <span className="flex items-center gap-2 text-green-600 text-sm">
                  <Check className="h-4 w-4" />
                  Preferences saved
                </span>
              )}
            </div>
            
            {/* Quick Actions */}
            <div className="flex flex-wrap gap-3 mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={acceptAll}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Accept All Cookies
              </button>
              <button
                onClick={rejectNonEssential}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 text-sm font-medium rounded-lg transition-colors"
              >
                Essential Only
              </button>
              <button
                onClick={() => savePreferences()}
                className="px-4 py-2 border border-teal-600 text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 text-sm font-medium rounded-lg transition-colors"
              >
                Save My Preferences
              </button>
            </div>

            {/* Cookie Categories */}
            <div className="space-y-4">
              {COOKIE_CATEGORIES.map((category) => {
                const Icon = category.icon;
                const isExpanded = expandedCategories.includes(category.id);
                const isEnabled = preferences[category.id];
                
                return (
                  <div
                    key={category.id}
                    className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden"
                  >
                    {/* Category Header */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50">
                      <button
                        onClick={() => toggleCategory(category.id)}
                        className="flex items-center gap-3 flex-1 text-left"
                      >
                        <div className={`p-2 rounded-lg ${isEnabled ? 'bg-teal-100 dark:bg-teal-900/30' : 'bg-gray-200 dark:bg-gray-700'}`}>
                          <Icon className={`h-5 w-5 ${isEnabled ? 'text-teal-600 dark:text-teal-400' : 'text-gray-500'}`} />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900 dark:text-white">{category.name}</h3>
                          <p className="text-xs text-gray-500">{category.cookies.length} cookie{category.cookies.length !== 1 ? 's' : ''}</p>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                      
                      {/* Toggle Switch */}
                      <button
                        onClick={() => togglePreference(category.id)}
                        disabled={category.required}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          isEnabled ? 'bg-teal-600' : 'bg-gray-300 dark:bg-gray-600'
                        } ${category.required ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            isEnabled ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    
                    {/* Category Details */}
                    {isExpanded && (
                      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{category.description}</p>
                        
                        {category.required && (
                          <div className="flex items-center gap-2 mb-4 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                            <Shield className="h-4 w-4 text-amber-600" />
                            <span className="text-xs text-amber-700 dark:text-amber-400">
                              These cookies are required and cannot be disabled
                            </span>
                          </div>
                        )}
                        
                        {/* Cookie Table */}
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-gray-200 dark:border-gray-700">
                                <th className="text-left py-2 pr-4 font-medium text-gray-700 dark:text-gray-300">Cookie Name</th>
                                <th className="text-left py-2 pr-4 font-medium text-gray-700 dark:text-gray-300">Purpose</th>
                                <th className="text-left py-2 font-medium text-gray-700 dark:text-gray-300">Duration</th>
                              </tr>
                            </thead>
                            <tbody>
                              {category.cookies.map((cookie, idx) => (
                                <tr key={idx} className="border-b border-gray-100 dark:border-gray-800 last:border-0">
                                  <td className="py-2 pr-4 text-gray-900 dark:text-white font-mono text-xs">{cookie.name}</td>
                                  <td className="py-2 pr-4 text-gray-600 dark:text-gray-400">{cookie.purpose}</td>
                                  <td className="py-2 text-gray-500">{cookie.duration}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.section>

          {/* Your Rights */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-white dark:bg-gray-900 rounded-2xl p-6 md:p-8 shadow-sm border border-gray-200 dark:border-gray-800 mb-6"
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Your Rights and Choices</h2>
            <div className="prose prose-gray dark:prose-invert max-w-none text-sm">
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                You have the right to decide whether to accept or reject cookies. You can exercise your cookie preferences by using 
                the preference manager above. Additionally, you can set or amend your web browser controls to accept or refuse cookies.
              </p>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mt-6 mb-3">Browser Controls</h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                Most web browsers allow some control of cookies through browser settings. To find out more about cookies, 
                including how to see what cookies have been set, visit <a href="https://www.aboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline inline-flex items-center gap-1">www.aboutcookies.org <ExternalLink className="h-3 w-3" /></a> or 
                <a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline inline-flex items-center gap-1 ml-1">www.allaboutcookies.org <ExternalLink className="h-3 w-3" /></a>.
              </p>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                Please note that if you choose to block cookies, you may not be able to access all or parts of our Website, 
                and some features may not function properly.
              </p>
            </div>
          </motion.section>

          {/* International Data Transfers */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-900 rounded-2xl p-6 md:p-8 shadow-sm border border-gray-200 dark:border-gray-800 mb-6"
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">International Data Transfers</h2>
            <div className="prose prose-gray dark:prose-invert max-w-none text-sm">
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                Information collected through cookies may be processed and stored in countries outside of Fiji, including 
                countries that may have different data protection laws than your country of residence. We take appropriate 
                safeguards to ensure that your personal data remains protected in accordance with this Cookie Policy.
              </p>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                By using our Website and accepting cookies, you consent to the transfer of information to countries outside 
                of Fiji, including to countries that may not provide the same level of data protection as Fiji.
              </p>
            </div>
          </motion.section>

          {/* Contact */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="bg-white dark:bg-gray-900 rounded-2xl p-6 md:p-8 shadow-sm border border-gray-200 dark:border-gray-800"
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Contact Us</h2>
            <div className="prose prose-gray dark:prose-invert max-w-none text-sm">
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                If you have any questions about our use of cookies or this Cookie Policy, please contact us:
              </p>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <p className="text-gray-900 dark:text-white font-medium">Bulaccino Cafe</p>
                <p className="text-gray-600 dark:text-gray-400">Nadi, Fiji</p>
                <p className="text-gray-600 dark:text-gray-400">Email: administrator@bulaccinocafe.com</p>
                <p className="text-gray-600 dark:text-gray-400">WhatsApp: +679 675 0102</p>
              </div>
            </div>
          </motion.section>
        </div>
      </main>
    </div>
  );
};

export default CookiePolicyPage;
